import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useEffectEvent, useMemo, useState } from 'react';

import { useLocation, useSearchParams } from 'react-router';

import { SHOW_SELECTED_URL_PARAM } from '@/constants/constants';
import { useAppSelector } from '@/hooks/redux-hooks';

import { extractFiltersFromParams } from '../lib/extractFiltersFromParams';
import { getEmptyFilterState } from '../lib/getEmptyFilterState';
import type { FilterState } from '../types/filter.types';

import { FilterContext } from './FilterContextBase';

export const FilterProvider: FC<{
  children: ReactNode;
  disableSearchParams?: boolean;
}> = ({ children, disableSearchParams = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Local state used only when disableSearchParams=true (e.g. modal — no URL interaction)
  const [localFilters, setLocalFilters] = useState<FilterState>(getEmptyFilterState);

  // URL is the source of truth for the main page; local state for the modal
  const filters: FilterState = useMemo(
    () => (disableSearchParams ? localFilters : extractFiltersFromParams(searchParams)),
    [disableSearchParams, localFilters, searchParams]
  );

  const isHaveEmptySymbols = useAppSelector(state => state.symbolSelector.symbols.length === 0);
  const location = useLocation();
  const showSelectedFilter = filters[SHOW_SELECTED_URL_PARAM];

  const clearShowSelectedFilter = useEffectEvent(() => {
    if (disableSearchParams) {
      setLocalFilters(prev => ({ ...prev, [SHOW_SELECTED_URL_PARAM]: false }));
    } else {
      setSearchParams(params => {
        const current = Object.fromEntries(params.entries());
        delete current[SHOW_SELECTED_URL_PARAM];
        return current;
      });
    }
  });

  const clearShowSelectedIfEmpty = useEffectEvent(() => {
    if (
      (location.pathname.includes('catalog') || location.pathname.includes('dashboard')) &&
      isHaveEmptySymbols &&
      showSelectedFilter
    ) {
      clearShowSelectedFilter();
    }
  });

  useEffect(() => {
    // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- Keep URL/local filter state in sync when navigating with no symbols selected.
    clearShowSelectedIfEmpty();
  }, [location.pathname]);

  const setFilter = useCallback(
    (key: string, value: string | null | boolean) => {
      const currentValue = filters[key as keyof FilterState];
      const isToggling =
        key !== 'search' && value !== null && String(value) === String(currentValue);
      const newValue = isToggling ? null : value;

      if (disableSearchParams) {
        setLocalFilters(prev => ({ ...prev, [key]: newValue }));
      } else {
        setSearchParams(params => {
          const current = Object.fromEntries(params.entries());
          if (newValue === null || newValue === '') {
            delete current[key];
          } else {
            current[key] = String(newValue);
          }
          return current;
        });
      }
    },
    [filters, setSearchParams, disableSearchParams]
  );

  const clearAllFilters = useCallback(() => {
    if (disableSearchParams) {
      setLocalFilters(getEmptyFilterState());
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [setSearchParams, disableSearchParams]);

  const isAnyFilterApplied = useMemo(() => {
    const entries = Object.entries(filters).filter(
      ([key]) => key !== SHOW_SELECTED_URL_PARAM && key !== 'search'
    );
    return !entries.every(([, value]) => value === null);
  }, [filters]);

  const value = useMemo(
    () => ({
      filters,
      clearAllFilters,
      setFilter,
      isAnyFilterApplied,
      disableSearchParams
    }),
    [filters, clearAllFilters, setFilter, isAnyFilterApplied, disableSearchParams]
  );

  return <FilterContext value={value}>{children}</FilterContext>;
};
